import { motion } from 'framer-motion';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';
import { HERO_PEOPLE_PHOTOS } from '@/data/homeContent.js';
import { cn } from '@/lib/cn.js';

/**
 * Bento grid placements — 4×3 on desktop, even 2×4 on mobile.
 * Index order matches HERO_PEOPLE_PHOTOS.
 *
 *   ┌──────────┬────┬────┐
 *   │    0     │ 1  │ 2  │
 *   │  (2×2)   ├────┼────┤
 *   │          │ 3  │ 4  │
 *   ├────┬─────┴────┤    │
 *   │ 5  │    6     │    │
 *   │    │  (2×1)   │    │
 *   └────┴──────────┴────┘
 */
const DESKTOP_SLOTS = [
  'sm:col-span-2 sm:row-span-2', // Slot 0: Big featured square
  'sm:col-span-1 sm:row-span-1', // Slot 1
  'sm:col-span-1 sm:row-span-1', // Slot 2
  'sm:col-span-1 sm:row-span-1', // Slot 3
  'sm:col-span-1 sm:row-span-2', // Slot 4: Tall "longer grid" of a woman
  'sm:col-span-1 sm:row-span-1', // Slot 5
  'sm:col-span-1 sm:row-span-1', // Slot 6
  'sm:col-span-1 sm:row-span-1', // Slot 7: Standard square (prevents layout overlap)
];

const tileMotion = {
  hidden: { opacity: 0, y: 16, scale: 0.94 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: 0.08 + i * 0.06, duration: 0.55, ease: [0.16, 1, 0.3, 1] },
  }),
};

function GoldCorner({ className }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute h-3 w-3 border-bangkok-gold/70',
        className,
      )}
    />
  );
}

function PhotoTile({ photo, index, className }) {
  const isFeatured = photo.featured || index === 0;

  return (
    <motion.div
      custom={index}
      variants={tileMotion}
      className={cn(
        'group relative min-h-0 overflow-hidden',
        'rounded-lg sm:rounded-xl',
        'bg-ink-900',
        'shadow-[0_4px_20px_rgba(0,0,0,0.4)]',
        'ring-1 ring-white/20',
        className,
      )}
    >
      <OptimizedImage
        src={photo.src}
        alt={photo.alt}
        priority={index < 4}
        className={cn(
          'h-full w-full object-cover',
          'brightness-[1.08] contrast-[1.06] saturate-[1.1]',
          'transition-transform duration-700 ease-out',
          isFeatured
            ? 'scale-[1.12] group-hover:scale-[1.18]'
            : 'scale-[1.15] group-hover:scale-[1.22]',
        )}
        style={{ objectPosition: photo.objectPosition ?? 'center 10%' }}
      />

      {/* Top highlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(255,255,255,0.14),transparent_55%)]"
      />
      {/* Bottom vignette for depth */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-950/35 via-transparent to-transparent"
      />
      {/* Inner frame */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/12',
          'transition-colors duration-300',
          'group-hover:ring-bangkok-gold/35',
        )}
      />
    </motion.div>
  );
}

/**
 * Homepage hero — eight delegate portraits in a polished bento grid.
 */
export function HeroPeopleMosaic({ className }) {
  return (
    <div className={cn('absolute inset-0 overflow-hidden', className)}>
      {/* Deep brand backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-[#060e1f] via-[#0c1a38] to-[#163a86]"
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-30 bg-thai-pattern"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_50%,rgba(28,68,156,0.35),transparent_60%)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_90%_80%,rgba(201,162,39,0.12),transparent_50%)]"
      />

      {/* Photo grid panel */}
      <div
        className={cn(
          'absolute z-[1] flex items-center justify-center',
          'inset-x-0 bottom-0 top-[34%]',
          'sm:inset-y-0 sm:left-[40%] sm:right-0',
          'md:left-[38%] lg:left-[36%]',
          'px-3 py-3 sm:px-5 sm:py-5 lg:px-7 lg:py-7',
        )}
      >
        <motion.div
          initial="hidden"
          animate="show"
          className="relative h-full w-full max-w-[880px]"
        >
          {/* Gold accent rules */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-3 left-4 right-4 hidden h-px sm:block bg-gradient-to-r from-transparent via-bangkok-gold/60 to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-3 left-4 right-4 hidden h-px sm:block bg-gradient-to-r from-transparent via-bangkok-gold/60 to-transparent"
          />

          {/* Glass frame */}
          <div
            className={cn(
              'relative h-full w-full',
              'rounded-2xl sm:rounded-3xl',
              'p-2 sm:p-2.5 lg:p-3',
              'bg-ink-950/40 ring-1 ring-bangkok-gold/25 backdrop-blur-sm',
              'shadow-[0_8px_48px_rgba(0,0,0,0.45),0_0_80px_rgba(201,162,39,0.08)]',
            )}
          >
            <GoldCorner className="left-2 top-2 border-l-2 border-t-2 rounded-tl-sm" />
            <GoldCorner className="right-2 top-2 border-r-2 border-t-2 rounded-tr-sm" />
            <GoldCorner className="bottom-2 left-2 border-b-2 border-l-2 rounded-bl-sm" />
            <GoldCorner className="bottom-2 right-2 border-b-2 border-r-2 rounded-br-sm" />

            <div
              className={cn(
                'h-full w-full',
                /* Mobile: even 2×4 */
                'grid grid-cols-2 grid-rows-4 gap-1.5',
                /* Desktop: 4×3 bento */
                'sm:grid-cols-4 sm:grid-rows-3 sm:gap-2 lg:gap-2.5',
              )}
            >
              {HERO_PEOPLE_PHOTOS.map((photo, i) => (
                <PhotoTile
                  key={photo.src}
                  photo={photo}
                  index={i}
                  className={DESKTOP_SLOTS[i]}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Text-column gradient — keeps copy legible without dimming the grid */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0 z-[2]',
          'w-full sm:w-[42%] md:w-[40%] lg:w-[38%]',
          'bg-gradient-to-r from-ink-950/97 via-ink-950/85 to-transparent',
        )}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 z-[2] h-[34%] sm:hidden bg-gradient-to-b from-ink-950/92 via-ink-950/55 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 z-[2] h-24 sm:hidden bg-gradient-to-t from-ink-950/70 to-transparent"
      />
    </div>
  );
}
